import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react'
import { type DropzoneState, useDropzone } from 'react-dropzone'
import { type DropzoneProps } from '../components/formControls/dropzone/Dropzone'
import {
	getNameExtensionFromFileName,
	replaceSpecialCharsWithDashes,
} from '../services/helpers'
import { renameFile } from '../components/formControls/dropzone/utils'

export type DropzoneContextValue = {
	files: File[]
	onRemoveFile: (file: File) => void
	onUpdateFileName: (file: File, newName: string) => void
	dropzoneState: DropzoneState
	color: string
	error?: boolean
	helperText?: string
	maxSize?: number
	maxFiles?: number
}

export type DropzoneProviderProps = {
	children: React.ReactNode
} & DropzoneProps

export const dropzoneColorMap = {
	primary: 'text-indigo-800 border-indigo-600 bg-indigo-50',
	success: 'text-green-800 border-green-600 bg-green-50',
	danger: 'text-red-800 border-red-600 bg-red-50',
}

export const DropzoneContext =
	createContext<DropzoneContextValue | null>(null)

export const useDropzoneContext = () => {
	const context = useContext(DropzoneContext)
	if (!context) {
		throw new Error(
			'useDropzoneContext must be used within a DropzoneProvider'
		)
	}
	return context
}

export const DropzoneProvider = ({
	children,
	onDropAccepted,
	error,
	helperText,
	maxSize,
	maxFiles,
	renameParts,
	defaultValue,
	...rest
}: DropzoneProviderProps) => {
	const [color, setColor] = useState(dropzoneColorMap.primary)
	const [files, setFiles] = useState<File[]>(defaultValue ?? [])

	const onDragEnter = () => {
		setColor(dropzoneColorMap.success)
	}

	const onDragLeave = () => {
		setColor(dropzoneColorMap.primary)
	}

	const onDrop = () => {
		setColor(dropzoneColorMap.primary)
	}

	const onAccepted = (dropFiles: File[]) => {
		const renamedDropFiles = dropFiles.map((file) => {
			return renameFile(file, renameParts)
		})

		const filteredDropFiles = renamedDropFiles.filter(
			(file) => !files.some((f) => f.name === file.name)
		)

		const allFiles = [...files, ...filteredDropFiles]

		setFiles(allFiles)
		onDropAccepted?.(allFiles)
	}

	const onErrorOrRejected = () => {
		setColor(dropzoneColorMap.danger)
	}

	const dropzoneState = useDropzone({
		onDrop,
		onDragEnter,
		onDragLeave,
		onDropRejected: onErrorOrRejected,
		onError: onErrorOrRejected,
		onDropAccepted: onAccepted,
		maxSize,
		maxFiles,
		...rest,
	})

	const handleRemoveFile = useCallback(
		(file: File) => {
			const newFiles = [...files]
			const index = newFiles.findIndex((f) => f.name === file.name)
			if (index !== -1) {
				newFiles.splice(index, 1)
				setFiles(newFiles)
				onDropAccepted?.(newFiles)
			}
		},
		[files, onDropAccepted]
	)

	const handleUpdateFileName = useCallback(
		(file: File, newName: string) => {
			const { name, extension } =
				getNameExtensionFromFileName(newName)
			const updatedFiles = files.map((f) =>
				f.name === file.name
					? new File(
							[file],
							`${replaceSpecialCharsWithDashes(name)}.${extension}`,
							{
								type: file.type,
							}
						)
					: f
			)
			setFiles(updatedFiles)
			onDropAccepted?.(updatedFiles)
		},
		[files, onDropAccepted]
	)

	const contextValue = useMemo(
		() => ({
			files,
			onRemoveFile: handleRemoveFile,
			onUpdateFileName: handleUpdateFileName,
			color,
			dropzoneState,
			error,
			helperText,
			maxSize,
			maxFiles,
		}),
		[
			files,
			handleRemoveFile,
			handleUpdateFileName,
			color,
			dropzoneState,
			error,
			helperText,
			maxSize,
			maxFiles,
		]
	)

	return (
		<DropzoneContext.Provider value={contextValue}>
			{children}
		</DropzoneContext.Provider>
	)
}
